import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL;

const inputStyle = { padding: '8px', borderRadius: '4px', border: '1px solid #ccc', width: '100%', boxSizing: 'border-box' };
const labelStyle = { fontSize: '12px', color: '#555', marginBottom: '3px', display: 'block' };
const btnStyle = (bg) => ({ padding: '6px 14px', background: bg, color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '13px' });

// recipient_type is stored as comma-separated string; form uses a Set for multi-select
const emptyTmpl = () => ({
  name: '', trigger_event: '', trigger_value: '', recipient_types: new Set(), subject: '', body_html: '', enabled: true,
});

// Convert DB string ↔ Set
const strToSet = (str) => new Set((str || '').split(',').map(s => s.trim()).filter(Boolean));
const setToStr = (set) => [...set].join(',');

// Events that support a trigger_value (status filter)
const STATUS_TRIGGER_EVENTS = ['program_status_changed'];

function VariableReference({ variables }) {
  const [open, setOpen] = useState(false);
  if (!variables || variables.length === 0) return null;

  // Support both old string format and new {name, description} format
  const vars = variables.map(v => typeof v === 'string' ? { name: v, description: '' } : v);

  return (
    <div style={{ border: '1px solid #e8ecef', borderRadius: '6px', marginBottom: '20px', overflow: 'hidden' }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        style={{ width: '100%', background: '#f8f9fa', border: 'none', padding: '9px 14px', textAlign: 'left', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
      >
        <span style={{ fontSize: '12px', fontWeight: '600', color: '#555' }}>
          Available template variables ({vars.length})
        </span>
        <span style={{ fontSize: '12px', color: '#999' }}>{open ? '▲ hide' : '▼ show'}</span>
      </button>
      {open && (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
          <thead>
            <tr style={{ background: '#f0f0f0' }}>
              <th style={{ padding: '6px 12px', textAlign: 'left', color: '#666', fontWeight: '600', width: '200px', borderBottom: '1px solid #e0e0e0' }}>Variable</th>
              <th style={{ padding: '6px 12px', textAlign: 'left', color: '#666', fontWeight: '600', borderBottom: '1px solid #e0e0e0' }}>Description</th>
            </tr>
          </thead>
          <tbody>
            {vars.map((v, i) => (
              <tr key={v.name} style={{ background: i % 2 === 0 ? 'white' : '#fafafa' }}>
                <td style={{ padding: '6px 12px', borderBottom: '1px solid #f0f0f0', whiteSpace: 'nowrap' }}>
                  <code style={{ background: '#e8ecef', padding: '1px 6px', borderRadius: '3px', fontSize: '12px', color: '#2c3e50' }}>{`{{${v.name}}}`}</code>
                </td>
                <td style={{ padding: '6px 12px', borderBottom: '1px solid #f0f0f0', color: '#555' }}>{v.description}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function EmailTemplatesEditor() {
  const [templates, setTemplates] = useState([]);
  const [meta, setMeta] = useState({ trigger_events: [], recipient_types: [], template_variables: [] });
  const [statusValues, setStatusValues] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyTmpl());
  const [testMsg, setTestMsg] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/email-templates/`).then(r => setTemplates(r.data)).catch(() => {});
    axios.get(`${API}/email-templates/meta`).then(r => setMeta(r.data)).catch(() => {});
    axios.get(`${API}/lookup-values/?category=program_status`).then(r => setStatusValues(r.data.map(v => v.value))).catch(() => {});
  }, []);

  const refresh = useCallback(() => axios.get(`${API}/email-templates/`).then(r => setTemplates(r.data)), []);

  const openNew = () => { setForm(emptyTmpl()); setEditing('new'); window.scrollTo({ top: 0, behavior: 'smooth' }); };
  const openEdit = (t) => {
    setForm({ ...t, trigger_value: t.trigger_value || '', recipient_types: strToSet(t.recipient_type) });
    setEditing(t.id);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const cancelEdit = () => { setEditing(null); setForm(emptyTmpl()); };

  const toggleRecipient = (val) => {
    setForm(prev => {
      const next = new Set(prev.recipient_types);
      next.has(val) ? next.delete(val) : next.add(val);
      return { ...prev, recipient_types: next };
    });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (form.recipient_types.size === 0) { alert('Select at least one recipient.'); return; }
    setSaving(true);
    const payload = {
      name: form.name,
      trigger_event: form.trigger_event,
      trigger_value: STATUS_TRIGGER_EVENTS.includes(form.trigger_event) ? (form.trigger_value || null) : null,
      recipient_type: setToStr(form.recipient_types),
      subject: form.subject,
      body_html: form.body_html,
      enabled: form.enabled,
    };
    try {
      if (editing === 'new') {
        await axios.post(`${API}/email-templates/`, payload);
      } else {
        await axios.put(`${API}/email-templates/${editing}`, payload);
      }
      await refresh();
      cancelEdit();
    } catch { /* ignore */ }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this email template?')) return;
    await axios.delete(`${API}/email-templates/${id}`);
    refresh();
  };

  const handleToggle = async (t) => {
    await axios.put(`${API}/email-templates/${t.id}`, { enabled: !t.enabled });
    refresh();
  };

  const handleTest = async (id) => {
    setTestMsg(prev => ({ ...prev, [id]: 'Sending…' }));
    try {
      const r = await axios.post(`${API}/email-templates/${id}/test`);
      setTestMsg(prev => ({ ...prev, [id]: r.data.success ? `✓ Sent to ${r.data.sent_to}` : '⚠ Skipped (SES not configured)' }));
    } catch {
      setTestMsg(prev => ({ ...prev, [id]: '✗ Error' }));
    }
    setTimeout(() => setTestMsg(prev => { const n = { ...prev }; delete n[id]; return n; }), 5000);
  };

  const triggerLabel = (t) => {
    const ev = meta.trigger_events.find(e => e.value === t.trigger_event);
    const base = ev?.label || t.trigger_event;
    return t.trigger_value ? `${base} → "${t.trigger_value}"` : base;
  };
  const recipientLabel = (val) =>
    (val || '').split(',').map(v => meta.recipient_types.find(r => r.value === v.trim())?.label || v.trim()).join(' + ');

  const showTriggerValue = STATUS_TRIGGER_EVENTS.includes(form.trigger_event);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div>
          <h3 style={{ margin: '0 0 4px' }}>Email Templates</h3>
          <p style={{ margin: 0, fontSize: '13px', color: '#7f8c8d' }}>
            Configure what emails get sent, to whom, and when. Use{' '}
            <code style={{ background: '#f0f0f0', padding: '1px 4px', borderRadius: '3px' }}>{'{{variable}}'}</code>{' '}
            placeholders in subject and body.
          </p>
        </div>
        <button onClick={openNew} style={btnStyle('#2c3e50')}>+ New Template</button>
      </div>

      {/* Variable reference — collapsible table */}
      <VariableReference variables={meta.template_variables} />

      {/* Edit / New form */}
      {editing !== null && (
        <form onSubmit={handleSave} style={{ background: '#f9f9f9', border: '1px solid #ddd', borderRadius: '8px', padding: '20px', marginBottom: '24px' }}>
          <h4 style={{ margin: '0 0 16px' }}>{editing === 'new' ? 'New Template' : 'Edit Template'}</h4>

          <div style={{ marginBottom: '12px' }}>
            <label style={labelStyle}>Template Name *</label>
            <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required style={{ ...inputStyle, fontSize: '13px' }} placeholder="e.g. Program Assigned — PM Alert" />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: showTriggerValue ? '1fr 1fr 1fr' : '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
            <div>
              <label style={labelStyle}>Trigger Event *</label>
              <select
                value={form.trigger_event}
                onChange={e => setForm({ ...form, trigger_event: e.target.value, trigger_value: '' })}
                required
                style={{ ...inputStyle, fontSize: '13px' }}
              >
                <option value="">— select —</option>
                {meta.trigger_events.map(ev => (
                  <option key={ev.value} value={ev.value}>{ev.label}</option>
                ))}
              </select>
            </div>

            {showTriggerValue && (
              <div>
                <label style={labelStyle}>Only when status is</label>
                <select
                  value={form.trigger_value}
                  onChange={e => setForm({ ...form, trigger_value: e.target.value })}
                  style={{ ...inputStyle, fontSize: '13px' }}
                >
                  <option value="">— any status change —</option>
                  {statusValues.map(sv => <option key={sv} value={sv}>{sv}</option>)}
                </select>
                <p style={{ margin: '3px 0 0', fontSize: '11px', color: '#999' }}>Leave blank to fire on every status change</p>
              </div>
            )}

            <div>
              <label style={labelStyle}>Send To * (select one or more)</label>
              <div style={{ border: '1px solid #ccc', borderRadius: '4px', padding: '8px 10px', background: 'white', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {meta.recipient_types.map(r => (
                  <label key={r.value} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={form.recipient_types.has(r.value)}
                      onChange={() => toggleRecipient(r.value)}
                    />
                    {r.label}
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div style={{ marginBottom: '12px' }}>
            <label style={labelStyle}>Subject *</label>
            <input value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })} required style={{ ...inputStyle, fontSize: '13px' }} placeholder="e.g. You've been assigned: {{program_name}}" />
          </div>
          <div style={{ marginBottom: '12px' }}>
            <label style={labelStyle}>Body (HTML) *</label>
            <textarea
              value={form.body_html}
              onChange={e => setForm({ ...form, body_html: e.target.value })}
              required
              rows={10}
              style={{ ...inputStyle, fontSize: '12px', fontFamily: 'monospace', resize: 'vertical' }}
              placeholder="<p>Hi {{pm_name}},</p><p>...</p>"
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <button type="submit" disabled={saving} style={btnStyle('#27ae60')}>{saving ? 'Saving…' : 'Save Template'}</button>
            <button type="button" onClick={cancelEdit} style={btnStyle('#95a5a6')}>Cancel</button>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer', marginLeft: '8px' }}>
              <input type="checkbox" checked={form.enabled} onChange={e => setForm({ ...form, enabled: e.target.checked })} />
              Enabled
            </label>
          </div>
        </form>
      )}

      {/* Template list — grouped by trigger category */}
      {templates.length === 0 && editing === null && (
        <p style={{ color: '#aaa', fontSize: '13px' }}>No templates yet. Create one above.</p>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {templates.map(t => (
          <div key={t.id} style={{ border: '1px solid #e8ecef', borderRadius: '8px', padding: '14px 18px', background: t.enabled ? 'white' : '#fafafa', opacity: t.enabled ? 1 : 0.65 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <strong style={{ fontSize: '14px' }}>{t.name}</strong>
                <div style={{ marginTop: '4px', fontSize: '12px', color: '#7f8c8d', display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                  <span>⚡ {triggerLabel(t)}</span>
                  <span>→ {recipientLabel(t.recipient_type)}</span>
                  <span style={{ fontStyle: 'italic' }}>{t.subject}</span>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0, marginLeft: '16px' }}>
                {testMsg[t.id] && (
                  <span style={{ fontSize: '12px', color: testMsg[t.id].startsWith('✓') ? '#27ae60' : testMsg[t.id].startsWith('⚠') ? '#e67e22' : '#e74c3c' }}>
                    {testMsg[t.id]}
                  </span>
                )}
                <button onClick={() => handleTest(t.id)} style={{ ...btnStyle('#8e44ad'), fontSize: '12px', padding: '4px 10px' }}>Test Send</button>
                <button onClick={() => handleToggle(t)} style={{ ...btnStyle(t.enabled ? '#27ae60' : '#95a5a6'), fontSize: '12px', padding: '4px 10px' }}>{t.enabled ? 'On' : 'Off'}</button>
                <button onClick={() => openEdit(t)} style={{ ...btnStyle('#3498db'), fontSize: '12px', padding: '4px 10px' }}>Edit</button>
                <button onClick={() => handleDelete(t.id)} style={{ ...btnStyle('#e74c3c'), fontSize: '12px', padding: '4px 10px' }}>Delete</button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


function EmailLog() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/email-templates/logs?limit=100`)
      .then(r => setLogs(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const STATUS_COLORS = { sent: '#27ae60', failed: '#e74c3c', skipped: '#e67e22' };

  return (
    <div style={{ marginTop: '48px', borderTop: '2px solid #e8ecef', paddingTop: '32px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <div>
          <h3 style={{ margin: '0 0 4px' }}>Email Log</h3>
          <p style={{ margin: 0, fontSize: '13px', color: '#7f8c8d' }}>Last 100 email send attempts.</p>
        </div>
        <button onClick={load} style={{ ...btnStyle('#7f8c8d'), fontSize: '12px' }}>Refresh</button>
      </div>

      {loading && <p style={{ color: '#aaa', fontSize: '13px' }}>Loading…</p>}
      {!loading && logs.length === 0 && <p style={{ color: '#aaa', fontSize: '13px' }}>No emails sent yet.</p>}
      {!loading && logs.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
            <thead>
              <tr style={{ background: '#f5f5f5' }}>
                <th style={{ padding: '8px 10px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Time</th>
                <th style={{ padding: '8px 10px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Template</th>
                <th style={{ padding: '8px 10px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Recipient</th>
                <th style={{ padding: '8px 10px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Subject</th>
                <th style={{ padding: '8px 10px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Status</th>
                <th style={{ padding: '8px 10px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Note</th>
              </tr>
            </thead>
            <tbody>
              {logs.map(log => (
                <tr key={log.id} style={{ borderBottom: '1px solid #eee' }}>
                  <td style={{ padding: '7px 10px', color: '#7f8c8d', whiteSpace: 'nowrap' }}>
                    {log.sent_at ? new Date(log.sent_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—'}
                  </td>
                  <td style={{ padding: '7px 10px' }}>{log.template_name || <span style={{ color: '#ccc' }}>—</span>}</td>
                  <td style={{ padding: '7px 10px' }}>
                    {log.recipient_name && <span>{log.recipient_name}<br /></span>}
                    <span style={{ color: '#7f8c8d' }}>{log.recipient_email || '—'}</span>
                  </td>
                  <td style={{ padding: '7px 10px', maxWidth: '240px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{log.subject_rendered || '—'}</td>
                  <td style={{ padding: '7px 10px' }}>
                    <span style={{ background: STATUS_COLORS[log.status] || '#ccc', color: 'white', borderRadius: '10px', padding: '2px 8px', fontSize: '11px' }}>
                      {log.status}
                    </span>
                  </td>
                  <td style={{ padding: '7px 10px', color: '#e74c3c', fontSize: '11px' }}>{log.error_message || ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


export default function EmailAlerts() {
  return (
    <div style={{ maxWidth: '1100px' }}>
      <div style={{ marginBottom: '28px' }}>
        <h2 style={{ margin: 0 }}>Email Alerts</h2>
        <p style={{ margin: '2px 0 0', fontSize: '13px', color: '#7f8c8d' }}>
          Manage automated email notifications for program events.
        </p>
      </div>

      <EmailTemplatesEditor />
      <EmailLog />
    </div>
  );
}
